import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Grupo } from '../types';
import { X, Plus, Edit2, Trash2, Save, Palette, Users, MapPin, Calendar } from 'lucide-react';

interface GroupManagementProps {
  onClose: () => void;
  onGroupsUpdated: () => void;
}

const PRESET_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'
];

export const GroupManagement: React.FC<GroupManagementProps> = ({
  onClose,
  onGroupsUpdated,
}) => {
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#3B82F6');
  const [editingGroup, setEditingGroup] = useState<Grupo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadGrupos();
  }, []);

  const loadGrupos = async () => {
    const { data, error } = await supabase
      .from('mapa_grupos')
      .select('*')
      .order('nombre');

    if (error) {
      console.error('Error loading groups:', error);
    } else {
      setGrupos(data || []);
    }
  };

  const addGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('mapa_grupos')
        .insert([{
          nombre: newGroupName.trim(),
          color: newGroupColor,
        }]);

      if (error) {
        console.error('Error completo:', error);
        alert(`Error al agregar el grupo: ${error.message || 'Error desconocido'}`);
      } else {
        console.log('Grupo agregado exitosamente');
        setNewGroupName('');
        setNewGroupColor('#3B82F6');
        loadGrupos();
        onGroupsUpdated();
      }
    } catch (error) {
      console.error('Error de red:', error);
      alert('Error de conexión. Verifica tu conexión a internet.');
    } finally {
      setLoading(false);
    }
  };

  const updateGroup = async (grupo: Grupo) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('mapa_grupos')
        .update({
          nombre: grupo.nombre,
          color: grupo.color,
        })
        .eq('id', grupo.id)
        .select();

      if (error) {
        console.error('Error updating group:', error);
        if (error.code === '42501') {
          alert('Error de permisos. Verifica la configuración de la base de datos.');
        } else {
          alert(`Error al actualizar el grupo: ${error.message}`);
        }
      } else {
        setEditingGroup(null);
        loadGrupos();
        onGroupsUpdated();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteGroup = async (id: string) => {
    if (window.confirm('¿Estás seguro? Esto eliminará el grupo y todas sus actividades.')) {
      setLoading(true);
      try {
        // Primero eliminar las actividades del grupo
        await supabase
          .from('mapa_actividades')
          .delete()
          .eq('grupo_id', id);

        // Luego eliminar el grupo
        const { error } = await supabase
          .from('mapa_grupos')
          .delete()
          .eq('id', id);

        if (error) {
          console.error('Error deleting group:', error);
          alert('Error al eliminar el grupo');
        } else {
          loadGrupos();
          onGroupsUpdated();
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Gestionar Grupos</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Formulario para nuevo grupo */}
          <form onSubmit={addGroup} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium mb-3">Nuevo Grupo</h3>
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Nombre del grupo"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Palette className="h-4 w-4 text-gray-600" />
                <div className="flex space-x-1">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewGroupColor(color)}
                      className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                        newGroupColor === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Agregar</span>
              </button>
            </div>
          </form>

          {/* Lista de grupos */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium">Grupos Existentes</h3>
            {grupos.map((grupo) => (
              <div
                key={grupo.id}
                className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow"
              >
                {editingGroup?.id === grupo.id ? (
                  <div className="flex flex-1 items-center space-x-3">
                    <input
                      type="text"
                      value={editingGroup.nombre}
                      onChange={(e) =>
                        setEditingGroup({ ...editingGroup, nombre: e.target.value })
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="flex space-x-1">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() =>
                            setEditingGroup({ ...editingGroup, color })
                          }
                          className={`w-6 h-6 rounded-full border transition-transform hover:scale-110 ${
                            editingGroup.color === color ? 'border-gray-800' : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => updateGroup(editingGroup)}
                      disabled={loading}
                      className="text-green-600 hover:text-green-700 transition-colors"
                    >
                      <Save className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setEditingGroup(null)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: grupo.color }}
                      ></div>
                      <span className="font-medium">{grupo.nombre}</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingGroup(grupo)}
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteGroup(grupo.id!)}
                        className="text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {grupos.length === 0 && (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No hay grupos creados</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};